import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { templatesApi, type Template, type VariableType } from "../api/templates";

const TYPES: VariableType[] = ["string", "number", "boolean"];

export function TemplateEditorPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const isNew = !templateId;
  const navigate = useNavigate();

  const [tpl, setTpl] = useState<Template>({
    templateId: "",
    name: "",
    subject: "",
    htmlBody: "",
    textBody: "",
    variableSchema: {},
    createdAt: "",
    updatedAt: "",
  });
  const [variables, setVariables] = useState<{ name: string; type: VariableType }[]>([]);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);

  useEffect(() => {
    if (!templateId) return;
    templatesApi.get(templateId).then((t) => {
      setTpl(t);
      setVariables(
        Object.entries(t.variableSchema).map(([name, type]) => ({ name, type })),
      );
    });
  }, [templateId]);

  const schema = useMemo<Record<string, VariableType>>(() => {
    const out: Record<string, VariableType> = {};
    for (const v of variables) if (v.name) out[v.name] = v.type;
    return out;
  }, [variables]);

  async function save() {
    const payload = { ...tpl, variableSchema: schema };
    if (isNew) {
      await templatesApi.create(payload);
    } else {
      await templatesApi.update(payload.templateId, payload);
    }
    navigate("/templates");
  }

  async function runPreview() {
    if (!templateId) return;
    const coerced: Record<string, unknown> = {};
    for (const v of variables) {
      const value = previewVars[v.name] ?? "";
      if (v.type === "number") coerced[v.name] = Number(value);
      else if (v.type === "boolean") coerced[v.name] = value === "true";
      else coerced[v.name] = value;
    }
    const res = await templatesApi.preview(templateId, coerced);
    setPreview(res);
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button component={RouterLink} to="/templates" size="small">
            ← All templates
          </Button>
          <Typography variant="h4" mt={1}>
            {isNew ? "New template" : tpl.name || templateId}
          </Typography>
        </Box>
        <Button variant="contained" onClick={save} disabled={!tpl.templateId || !tpl.name}>
          Save
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Template ID"
                  value={tpl.templateId}
                  onChange={(e) =>
                    setTpl((t) => ({ ...t, templateId: e.target.value.toLowerCase() }))
                  }
                  disabled={!isNew}
                  helperText="lowercase, alphanumeric and dashes"
                />
                <TextField
                  label="Name"
                  value={tpl.name}
                  onChange={(e) => setTpl((t) => ({ ...t, name: e.target.value }))}
                />
                <TextField
                  label="Subject"
                  value={tpl.subject}
                  onChange={(e) => setTpl((t) => ({ ...t, subject: e.target.value }))}
                />
                <TextField
                  label="HTML body"
                  value={tpl.htmlBody}
                  onChange={(e) => setTpl((t) => ({ ...t, htmlBody: e.target.value }))}
                  multiline
                  minRows={10}
                  sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                />
                <TextField
                  label="Text body (optional)"
                  value={tpl.textBody ?? ""}
                  onChange={(e) => setTpl((t) => ({ ...t, textBody: e.target.value }))}
                  multiline
                  minRows={5}
                  sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={1}>
                Variables
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={2} display="block">
                Reference inside the template as <code>{"{{name}}"}</code>.
              </Typography>
              <Stack spacing={1}>
                {variables.map((v, i) => (
                  <Stack key={i} direction="row" spacing={1}>
                    <TextField
                      label="Name"
                      size="small"
                      value={v.name}
                      onChange={(e) => {
                        const next = [...variables];
                        next[i] = { ...next[i], name: e.target.value } as typeof next[number];
                        setVariables(next);
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      select
                      label="Type"
                      size="small"
                      value={v.type}
                      onChange={(e) => {
                        const next = [...variables];
                        next[i] = { ...next[i], type: e.target.value as VariableType } as typeof next[number];
                        setVariables(next);
                      }}
                      sx={{ width: 130 }}
                    >
                      {TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton
                      onClick={() => setVariables(variables.filter((_, j) => j !== i))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small"
                  onClick={() => setVariables([...variables, { name: "", type: "string" }])}
                >
                  + Add variable
                </Button>
              </Stack>

              {!isNew && (
                <Box mt={3}>
                  <Typography variant="h6" mb={1}>
                    Preview
                  </Typography>
                  <Stack spacing={1}>
                    {variables.map((v) => (
                      <TextField
                        key={v.name}
                        size="small"
                        label={`${v.name} (${v.type})`}
                        value={previewVars[v.name] ?? ""}
                        onChange={(e) =>
                          setPreviewVars((p) => ({ ...p, [v.name]: e.target.value }))
                        }
                      />
                    ))}
                    <Button onClick={runPreview} variant="outlined">
                      Render
                    </Button>
                  </Stack>
                  {preview && (
                    <Box mt={2}>
                      <Typography variant="subtitle2">Subject</Typography>
                      <Typography mb={2}>{preview.subject}</Typography>
                      <Typography variant="subtitle2">HTML</Typography>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 2,
                          bgcolor: "background.default",
                        }}
                        dangerouslySetInnerHTML={{ __html: preview.html }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
